import { useFormAction, useNavigation } from '@remix-run/react';

export const WORKSPACE_DETAIL_IMAGE_SIZE = '1220x780'

/**
 * Returns true if the current navigation is submitting the current route's
 * form. Defaults to the current route's form action and method POST.
 *
 * Defaults state to 'non-idle'
 *
 * NOTE: the default formAction will include query params, but the
 * navigation.formAction will not, so don't use the default formAction if you
 * want to know if a form is submitting without specific query params.
 */
export function useIsPending({
	formAction,
	formMethod = 'POST',
	state = 'non-idle',
}: {
	formAction?: string
	formMethod?: 'POST' | 'GET' | 'PUT' | 'PATCH' | 'DELETE'
	state?: 'submitting' | 'loading' | 'non-idle'
} = {}) {
	const contextualFormAction = useFormAction()
	const navigation = useNavigation()
	const isPendingState =
		state === 'non-idle'
			? navigation.state !== 'idle'
			: navigation.state === state
	return (
		isPendingState &&
		navigation.formAction === (formAction ?? contextualFormAction) &&
		navigation.formMethod === formMethod
	)
}

interface CalculateEndDateParams {
  startDate: Date;
  daysToAdd: number;
}

export function calculateEndDate({ startDate, daysToAdd }: CalculateEndDateParams): Date {
  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + daysToAdd);
  return endDate;
}

/**
 * Returns the amount to a floating-point number and then to cents
 */
export function convertEuroToCents(amount: string): number {
	return Math.round(parseFloat(amount) * 100);
}

/**
 * Converts an amount in cents to euros as a floating-point number
 */
export function convertCentsToEuro(cents: number): string {
  return (cents / 100).toFixed(2);
}

/**
 * A plan can only be switched to via the paid recurring-subscription flow when it has a
 * real charge (price > 0) AND a real billing period (interval > 0). The €0 / interval-0
 * "free" and "management-plan" products are "contact us" tiers that Paycomet cannot bill,
 * so they must not appear in the change-plan modal. Kept in sync with the server-side guard
 * of the same name in app/.server/utils/misc.ts (checkout.updateSubscription).
 */
export function isChargeableRecurringPlan(plan: { price: number; interval: number }): boolean {
  return plan.price > 0 && plan.interval > 0;
}

/**
 * Formats a price with the appropriate currency symbol using Intl API
 */
export function formatPrice(cents: number, currency: string = 'EUR'): string {
  const amount = cents / 100;
  return new Intl.NumberFormat(currency === 'USD' ? 'en-US' : 'es-ES', {
    style: 'currency',
    currency,
  }).format(amount);
}

export type CalculateItemsShownParams = {
  currentPage: number;
  totalCount: number;
  pageSize: number;
};

export function calculateNumberOfItemsShown({ currentPage, totalCount, pageSize }: CalculateItemsShownParams): string {
  const start = (currentPage - 1) * pageSize + 1;
  const end = Math.min(start + pageSize - 1, totalCount);
  return `${start}-${end}`;
}

export const getImageUrl = (filename: string = 'jhkj4h2jk2kj2233.jpg', dimensions: string = WORKSPACE_DETAIL_IMAGE_SIZE) => {
  const baseUrl = 'https://images.compartirespacios.com'; // TODO USE
  return dimensions ? `${baseUrl}/${dimensions}/${filename}` : `${baseUrl}/${filename}`;
};
